import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  getQuestionQueue, queueShow, queueNext, queueSkip, queueDone,
  queuePriority, queueClear, queuePause,
  QuestionQueueState, QuestionItem,
} from './api';

function tkImg(u?: string | null): string {
  if (!u) return '';
  if (/tiktokcdn|ibyteimg|muscdn|bytecdn|tiktok\.com/i.test(u))
    return '/api/gift-image?url=' + encodeURIComponent(u);
  return u;
}

interface Props { streamerId: number; isMobile?: boolean; readOnly?: boolean; }

export default function QuestionQueuePanel({ streamerId, isMobile, readOnly }: Props) {
  const [st, setSt] = useState<QuestionQueueState | null>(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try { const r = await getQuestionQueue(streamerId); setSt(r.data); } catch {}
  }, [streamerId]);

  useEffect(() => {
    load();
    timer.current = setInterval(load, 3000);
    return () => { if (timer.current) clearInterval(timer.current); };
  }, [load]);

  if (!st || !st.enabled) return null;

  const act = async (fn: () => Promise<any>) => {
    if (busy || readOnly) return;
    setBusy(true);
    try { await fn(); await load(); } finally { setBusy(false); }
  };

  const items = st.items || [];
  const active = st.active || items.find(i => i.status === 'active') || null;
  const waiting = items.filter(i => i.status === 'waiting');

  const card: React.CSSProperties = {
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
    padding: isMobile ? 12 : 16, marginBottom: 16,
  };
  const btn = (bg: string): React.CSSProperties => ({
    padding: '7px 12px', borderRadius: 8, border: 'none', background: bg,
    color: '#fff', fontSize: 12, fontWeight: 600, cursor: busy ? 'wait' : 'pointer',
    opacity: busy ? 0.6 : 1, whiteSpace: 'nowrap',
  });
  const ghost: React.CSSProperties = {
    padding: '6px 10px', borderRadius: 7, border: '1px solid #e2e8f0',
    background: '#f8fafc', color: '#475569', fontSize: 12, cursor: 'pointer',
  };

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>📋 File d'attente des questions</span>
        <span style={{ background: '#eff6ff', color: '#2563eb', borderRadius: 999, padding: '2px 9px', fontSize: 12, fontWeight: 700 }}>
          {waiting.length} en attente
        </span>
        {st.paused
          ? <span style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 999, padding: '2px 9px', fontSize: 12, fontWeight: 700 }}>⏹ Arrêtée</span>
          : <span style={{ background: '#dcfce7', color: '#15803d', borderRadius: 999, padding: '2px 9px', fontSize: 12, fontWeight: 700 }}>▶ En cours</span>}
        <div style={{ flex: 1 }} />
        {!readOnly && (
          <>
            <button style={btn(st.paused ? '#16a34a' : '#dc2626')} onClick={() => act(() => queuePause(streamerId, !st.paused))}>
              {st.paused ? '▶ Démarrer' : '⏹ Arrêter'}
            </button>
            <button style={ghost} onClick={() => { if (window.confirm('Vider toute la file ?')) act(() => queueClear(streamerId)); }}>
              🗑 Vider
            </button>
          </>
        )}
      </div>


      {/* Question affichée */}
      <div style={{
        border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, marginBottom: 12,
        background: active ? '#f0fdf4' : '#f8fafc',
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6 }}>
          {active ? '🟢 À L\'ÉCRAN' : 'Aucune question affichée'}
        </div>
        {active && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              {active.avatar_url && <img src={tkImg(active.avatar_url)} alt="" style={{ width: 26, height: 26, borderRadius: '50%', objectFit: 'cover' }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />}
              <span style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>{active.nickname || active.username}</span>
              {!!active.coins && <span style={{ fontSize: 11, color: '#92400e' }}>🎁 {active.coins}</span>}
            </div>
            <div style={{ fontSize: 14, color: '#0f172a', lineHeight: 1.35 }}>{active.question}</div>
            {!readOnly && (
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                <button style={btn('#16a34a')} onClick={() => act(() => queueNext(streamerId))}>⏭ Question suivante</button>
                <button style={btn('#64748b')} onClick={() => act(() => queueDone(streamerId, active.id))}>✓ Terminé</button>
              </div>
            )}
          </>
        )}
        {!active && !readOnly && waiting.length > 0 && (
          <button style={{ ...btn('#2563eb'), marginTop: 4 }} onClick={() => act(() => queueShow(streamerId))}>
            ▶ Afficher la première
          </button>
        )}
      </div>

      {/* Liste en attente */}
      {waiting.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 13, padding: '14px 0' }}>
          Aucune question en attente
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {waiting.map((q: QuestionItem, idx: number) => (
            <div key={q.id} style={{
              display: 'flex', alignItems: 'flex-start', gap: 8, padding: 10,
              border: '1px solid #f1f5f9', borderRadius: 9,
              background: q.priority ? '#fffbeb' : '#fff',
            }}>
              <span style={{ fontWeight: 800, color: '#cbd5e1', fontSize: 13, minWidth: 18 }}>{idx + 1}</span>
              {q.avatar_url && <img src={tkImg(q.avatar_url)} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 12, color: '#0f172a' }}>
                  {q.nickname || q.username}
                  {q.priority ? <span title="Prioritaire"> ⭐</span> : null}
                  {!!q.coins && <span style={{ color: '#92400e', fontWeight: 400, marginLeft: 6 }}>🎁 {q.coins}</span>}
                </div>
                <div style={{ fontSize: 13, color: '#334155', marginTop: 2, lineHeight: 1.3 }}>{q.question}</div>
                {!readOnly && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 7, flexWrap: 'wrap' }}>
                    <button style={btn('#2563eb')} onClick={() => act(() => queueShow(streamerId, q.id))}>Afficher</button>
                    <button style={ghost} onClick={() => act(() => queuePriority(streamerId, q.id))}>{q.priority ? 'Retirer priorité' : '⭐ Priorité'}</button>
                    <button style={ghost} onClick={() => act(() => queueSkip(streamerId, q.id))}>Ignorer</button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Aperçu en direct de la file d'attente (rendu local, sans OBS) ── */
const SAMPLE = [
  { n: 'Marie 🌸', p: true },
  { n: 'Kevin', p: false },
  { n: 'Sofia', p: false },
];

function qFileUrl(n?: string): string {
  return n ? `/api/animation-file/${n}` : '';
}

// Position d'un libellé autour de la boîte cadeau (image en absolute inset:0)
export function gLabelStyle(pos?: string): React.CSSProperties {
  switch (pos) {
    case 'above': return { left: '-25%', right: '-25%', bottom: '100%', textAlign: 'center', marginBottom: 3 };
    case 'below': return { left: '-25%', right: '-25%', top: '100%', textAlign: 'center', marginTop: 3 };
    case 'left': return { right: '100%', top: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginRight: 4 };
    case 'right': return { left: '100%', top: 0, bottom: 0, display: 'flex', alignItems: 'center', marginLeft: 4 };
    default: return { display: 'none' };
  }
}

export function QueuePreview({ config, tick, bare }: { config: Record<string, any>; tick: number; bare?: boolean }) {
  const c = config || {};
  const num = (v: any, d: number) => (v === undefined || v === null || v === '' ? d : Number(v));
  const truthy = (v: any) => v !== 0 && v !== false && v !== undefined && v !== null;

  const W = 200, H = 356;
  const lx = num(c.pos_x, 2), ly = num(c.pos_y, 55), lw = num(c.pos_w, 34), lh = num(c.pos_h, 42);
  const qx = num(c.queue_q_pos_x, 15), qy = num(c.queue_q_pos_y, 38), qw = num(c.queue_q_pos_w, 70), qh = num(c.queue_q_pos_h, 24);
  const cx = num(c.queue_ctrl_pos_x, 2), cy = num(c.queue_ctrl_pos_y, 89), cw = num(c.queue_ctrl_pos_w, 34), ch = num(c.queue_ctrl_pos_h, 8);
  const color = c.text_color || '#fff';
  const font = c.text_font || 'sans-serif';
  const tsize = num(c.text_size, 40);
  const listFs = Math.max(7, Math.round(tsize * 0.13));
  const questionText = "Marie 🌸 Est-ce que je vais retrouver l'amour ?";
  const maxLines = qh < 13 ? 1 : qh < 21 ? 2 : 3;
  const questionCapacity = Math.max(8, qw * 1.18 * maxLines);
  const questionBase = Math.min(tsize * 0.16, qh * 0.68);
  const qFs = Math.max(8, Math.round(Math.min(tsize * 0.16, questionText.length > questionCapacity ? questionBase * (questionCapacity / questionText.length) : questionBase)));
  const showAvatar = truthy(c.queue_show_avatar);
  const showCounter = truthy(c.queue_show_counter);
  const appear = c.queue_appear_anim || 'fade';
  const bgImg = c.queue_bg_image ? qFileUrl(c.queue_bg_image) : '';
  const qBgImg = c.queue_q_bg_image ? qFileUrl(c.queue_q_bg_image) : '';
  const bgCol = c.queue_q_bg_color || c.text_bg_color || '#111111';
  const op = Math.max(0, Math.min(100, num(c.text_bg_opacity, 60))) / 100;
  const showGift = truthy(c.queue_show_gift) && c.gift_image_url;
  const showPGift = truthy(c.queue_show_pgift) && c.priority_gift_image_url;
  const gpx = num(c.queue_gift_pos_x, 70), gpy = num(c.queue_gift_pos_y, 5), gpw = num(c.queue_gift_pos_w, 18), gph = num(c.queue_gift_pos_h, 10);
  const ppx = num(c.queue_pgift_pos_x, 70), ppy = num(c.queue_pgift_pos_y, 18), ppw = num(c.queue_pgift_pos_w, 18), pph = num(c.queue_pgift_pos_h, 10);

  const inner = (
    <>
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes qpv-fade{from{opacity:0}to{opacity:1}}
        @keyframes qpv-zoom{from{opacity:0;transform:scale(.5)}to{opacity:1;transform:scale(1)}}
        @keyframes qpv-slide{from{opacity:0;transform:translateY(30px)}to{opacity:1;transform:translateY(0)}}
        @keyframes qpv-flip{from{opacity:0;transform:perspective(500px) rotateX(90deg)}to{opacity:1;transform:perspective(500px) rotateX(0)}}
      ` }} />

      {/* Liste */}
      <div style={{
        position: 'absolute', left: `${lx}%`, top: `${ly}%`, width: `${lw}%`, maxHeight: `${lh}%`,
        display: 'flex', flexDirection: 'column', gap: 3, boxSizing: 'border-box',
        fontFamily: font, color, fontSize: listFs,
        padding: bgImg ? '7% 9%' : 0,
        background: bgImg ? `url(${bgImg}) center/100% 100% no-repeat` : 'transparent',
        overflow: 'hidden',
      }}>
        {showCounter && <div style={{ fontWeight: 800, textShadow: '0 1px 2px rgba(0,0,0,0.7)' }}>💬 File · {SAMPLE.length}</div>}
        {SAMPLE.map((s, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'rgba(0,0,0,0.3)', borderRadius: 99, padding: '2px 6px' }}>
            <span style={{ fontWeight: 800, opacity: 0.7 }}>{i + 1}</span>
            {showAvatar && <span style={{ width: '1.4em', height: '1.4em', borderRadius: '50%', background: 'rgba(255,255,255,0.35)', flex: 'none' }} />}
            <span style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}>{s.n}</span>
            {s.p && <span>⭐</span>}
          </div>
        ))}
      </div>

      {/* Question (rejouée à chaque test via key={tick}) */}
      <div key={tick} style={{
        position: 'absolute', left: `${qx}%`, top: `${qy}%`, width: `${qw}%`, height: `${qh}%`,
        boxSizing: 'border-box', borderRadius: 14, overflow: 'hidden',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5,
        padding: '6%', textAlign: 'center', fontFamily: font, color,
        background: qBgImg ? `url(${qBgImg}) center/100% 100% no-repeat`
          : `rgba(${parseInt((bgCol).slice(1, 3) || '11', 16)},${parseInt((bgCol).slice(3, 5) || '11', 16)},${parseInt((bgCol).slice(5, 7) || '11', 16)},${op || 0.85})`,
        animation: tick > 0 ? `qpv-${appear} .5s ease both` : undefined,
        opacity: tick > 0 ? undefined : 0.55,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          {showAvatar && <span style={{ width: '1.7em', height: '1.7em', borderRadius: '50%', background: 'rgba(255,255,255,0.35)' }} />}
          <span style={{ fontWeight: 800, fontSize: qFs, textShadow: '0 1px 3px rgba(0,0,0,0.75)' }}>Marie 🌸</span>
        </div>
        <span style={{ fontWeight: 800, fontSize: qFs + 2, lineHeight: 1.25, textShadow: '0 1px 3px rgba(0,0,0,0.75)' }}>
          Est-ce que je vais retrouver l'amour&nbsp;?
        </span>
      </div>

      {showGift && (
        <div style={{ position: 'absolute', left: `${gpx}%`, top: `${gpy}%`, width: `${gpw}%`, height: `${gph}%`, overflow: 'visible' }}>
          <img src={tkImg(c.gift_image_url)} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', filter: 'drop-shadow(0 2px 6px rgba(0,0,0,.55))' }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          {c.queue_gift_label && (c.queue_gift_label_pos || 'none') !== 'none' && (
            <span style={{ position: 'absolute', whiteSpace: 'nowrap', fontWeight: 800, color: c.queue_gift_label_color || '#fff', fontSize: Math.max(7, Math.round(num(c.queue_gift_label_size, 14) * 0.32)), fontFamily: font, textShadow: '0 1px 2px rgba(0,0,0,.7)', ...gLabelStyle(c.queue_gift_label_pos) }}>{c.queue_gift_label}</span>
          )}
        </div>
      )}
      {showPGift && (
        <div style={{ position: 'absolute', left: `${ppx}%`, top: `${ppy}%`, width: `${ppw}%`, height: `${pph}%`, overflow: 'visible' }}>
          <img src={tkImg(c.priority_gift_image_url)} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', filter: 'drop-shadow(0 2px 6px rgba(0,0,0,.55))' }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          {c.queue_pgift_label && (c.queue_pgift_label_pos || 'none') !== 'none' && (
            <span style={{ position: 'absolute', whiteSpace: 'nowrap', fontWeight: 800, color: c.queue_pgift_label_color || '#fff', fontSize: Math.max(7, Math.round(num(c.queue_pgift_label_size, 14) * 0.32)), fontFamily: font, textShadow: '0 1px 2px rgba(0,0,0,.7)', ...gLabelStyle(c.queue_pgift_label_pos) }}>{c.queue_pgift_label}</span>
          )}
        </div>
      )}

      {/* Boutons (gris translucides, discrets comme sur l'overlay) */}
      <div style={{ position: 'absolute', left: `${cx}%`, top: `${cy}%`, width: `${cw}%`, height: `${ch}%`, display: 'flex', gap: 5 }}>
        <div style={{ flex: 1, background: 'rgba(70,70,78,0.34)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.78)', fontSize: Math.min(ch * 2.4, 14) }}>▶</div>
        <div style={{ flex: 1, background: 'rgba(70,70,78,0.34)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.78)', fontSize: Math.min(ch * 2.4, 14) }}>⏭</div>
      </div>
    </>
  );
  if (bare) return inner;
  return (
    <div style={{ position: 'relative', width: W, height: H, background: '#0a0a0a', borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0', flexShrink: 0 }}>
      {inner}
    </div>
  );
}
